import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Tag, Clock } from 'lucide-react';
import { Helmet } from 'react-helmet';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/articles/${slug}`);
        setArticle(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Article not found' : 'Failed to load article');
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  // Estimate reading time from HTML content
  const readingTime = useMemo(() => {
    if (!article?.body) return 0;
    const text = article.body.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  }, [article?.body]);

  // Extract headings for table of contents
  const toc = useMemo(() => {
    if (!article?.body) return [];
    const matches = [...article.body.matchAll(/<h([23])[^>]*>(.*?)<\/h[23]>/gi)];
    return matches.map((m, i) => ({
      level: parseInt(m[1]),
      text: m[2].replace(/<[^>]*>/g, ''),
      id: `heading-${i}`,
    }));
  }, [article?.body]);

  // Inject IDs into headings for anchor links
  const bodyWithIds = useMemo(() => {
    if (!article?.body) return '';
    let idx = 0;
    return article.body.replace(/<h([23])([^>]*)>/gi, (match, level, attrs) => {
      const id = `heading-${idx++}`;
      return `<h${level}${attrs} id="${id}">`;
    });
  }, [article?.body]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#2E4033] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex flex-col items-center justify-center gap-4">
        <p className="text-gray-500 text-lg">{error || 'Article not found'}</p>
        <Link to="/articles" className="text-[#2E4033] font-medium hover:underline">
          <ArrowLeft size={16} className="inline mr-1" />Back to Articles
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{`${article.title} | ContractWiser`}</title>
        <meta name="description" content={article.meta_description || article.excerpt || ''} />
        <meta name="keywords" content={(article.seo_tags || []).join(', ')} />
        <meta name="author" content={article.author || 'ContractWiser'} />
        <link rel="canonical" href={`https://contractwiser.com.au/articles/${article.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.meta_description || article.excerpt || ''} />
        <meta property="og:url" content={`https://contractwiser.com.au/articles/${article.slug}`} />
        {article.featured_image_url && <meta property="og:image" content={article.featured_image_url} />}
        <meta property="article:published_time" content={article.published_at || ''} />
        <meta property="article:author" content={article.author || 'ContractWiser'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.meta_description || article.excerpt || ''} />
        {article.featured_image_url && <meta name="twitter:image" content={article.featured_image_url} />}
      </Helmet>
      
      {/* JSON-LD Schema - rendered outside Helmet to avoid issues */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.meta_description || article.excerpt,
            "image": article.featured_image_url || undefined,
            "author": { "@type": "Organization", "name": article.author || "ContractWiser" },
            "publisher": { "@type": "Organization", "name": "ContractWiser", "url": "https://contractwiser.com.au" },
            "datePublished": article.published_at,
            "dateModified": article.updated_at,
            "mainEntityOfPage": `https://contractwiser.com.au/articles/${article.slug}`,
          })
        }}
      />

      {/* Header */}
      <header className="bg-[#2E4033] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/contractwiser-logo.svg" alt="ContractWiser" className="h-8 w-8" />
            <span className="font-semibold text-lg tracking-tight">ContractWiser</span>
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/upload" className="text-sm text-white/80 hover:text-white transition-colors">Upload Contract</Link>
            <Link to="/articles" className="text-sm text-white font-medium">Articles</Link>
          </nav>
        </div>
      </header>

      {/* Featured Image */}
      {article.featured_image_url && (
        <div className="w-full h-64 md:h-96 overflow-hidden">
          <img
            src={article.featured_image_url}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <main className="max-w-4xl mx-auto px-4 py-10">
        {/* Breadcrumb */}
        <Link to="/articles" className="inline-flex items-center gap-1.5 text-sm text-[#2E4033] hover:underline mb-6" data-testid="article-back-link">
          <ArrowLeft size={14} />Back to Articles
        </Link>

        {/* Article Header */}
        <article>
          {article.category && (
            <span className="inline-block px-3 py-1 bg-[#2E4033]/10 text-[#2E4033] text-xs font-medium rounded-full mb-4">
              <Tag size={11} className="inline mr-1 -mt-0.5" />{article.category}
            </span>
          )}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight" data-testid="article-title">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-8 pb-8 border-b border-gray-200">
            <span className="flex items-center gap-1.5">
              <User size={14} />{article.author || 'ContractWiser'}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar size={14} />{formatDate(article.published_at)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={14} />{readingTime} min read
            </span>
          </div>

          {/* Table of Contents */}
          {toc.length > 2 && (
            <nav className="bg-white border border-gray-200 rounded-xl p-6 mb-10" data-testid="article-toc">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">Table of Contents</h3>
              <ul className="space-y-1.5">
                {toc.map(item => (
                  <li key={item.id} className={item.level === 3 ? 'ml-4' : ''}>
                    <a
                      href={`#${item.id}`}
                      className="text-sm text-gray-600 hover:text-[#2E4033] transition-colors"
                    >
                      {item.text}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {/* Article Body */}
          <div
            className="prose prose-lg max-w-none
              prose-headings:text-gray-900 prose-headings:font-semibold
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-gray-700 prose-p:leading-relaxed
              prose-a:text-[#2E4033] prose-a:font-medium
              prose-strong:text-gray-900
              prose-ul:text-gray-700 prose-ol:text-gray-700
              prose-blockquote:border-l-[#2E4033] prose-blockquote:text-gray-600
              prose-img:rounded-xl prose-img:shadow-md"
            dangerouslySetInnerHTML={{ __html: bodyWithIds }}
            data-testid="article-body"
          />

          {/* Tags */}
          {article.seo_tags && article.seo_tags.length > 0 && (
            <div className="mt-10 pt-8 border-t border-gray-200">
              <div className="flex flex-wrap gap-2">
                {article.seo_tags.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>

      {/* CTA */}
      <section className="bg-[#2E4033] text-white py-16 mt-10">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Need your contract reviewed?</h2>
          <p className="text-white/70 mb-6">Get a plain-English analysis report of your Australian property contract in minutes.</p>
          <Link
            to="/upload"
            className="inline-flex items-center gap-2 bg-white text-[#2E4033] px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
          >
            Upload Contract
          </Link>
        </div>
      </section>
    </div>
  );
}
